import React from "react";
import { Receipt, Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { api } from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";

const fmt = (v) => (v != null ? `${Number(v).toFixed(2)} DH` : "—");

const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" }) : "—";

/**
 * Liste des prix d'achat d'un produit stockable, extraite des bons de
 * réception confirmés (GET /products/{id}/purchase-prices), avec export Excel.
 */
const PurchasePricesSection = ({ productId, productName }) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["purchase-prices", productId],
        queryFn: async () => {
            const account = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/products/${productId}/purchase-prices`, {
                headers: { "X-account-id": account.id },
            });
            return response.data;
        },
        enabled: !!productId,
    });

    const rows = Array.isArray(data?.data) ? data.data : [];
    const summary = data?.summary || {};
    const hasVariants = rows.some((r) => r.variant_name);

    const handleExport = () => {
        const sheet = XLSX.utils.json_to_sheet(
            rows.map((r) => ({
                Date: r.date || "",
                Référence: r.reference || "",
                Fournisseur: r.provider_name || "",
                ...(hasVariants ? { Variante: r.variant_name || "" } : {}),
                Quantité: r.quantity,
                "Prix unitaire HT": r.unit_cost_ht ?? "",
                "Prix unitaire TTC": r.unit_cost_ttc ?? "",
                "Total HT": r.total_ht ?? "",
                "Total TTC": r.total_ttc ?? "",
            }))
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, "Prix d'achat");
        const safeName = (productName || "produit").replace(/[^\w-]+/g, "_");
        XLSX.writeFile(wb, `prix_achat_${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Receipt className="w-3 h-3" />
                    Prix d'achat
                    {rows.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-[10px]">
                            {rows.length}
                        </span>
                    )}
                </p>
                {rows.length > 0 && (
                    <button
                        onClick={handleExport}
                        title="Exporter les prix d'achat"
                        className="flex items-center gap-1.5 px-3 h-7 rounded-lg text-xs font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Exporter
                    </button>
                )}
            </div>

            {isLoading && <p className="text-xs text-gray-400">Chargement...</p>}
            {error && <p className="text-xs text-red-500">Erreur lors du chargement des prix d'achat</p>}

            {data && rows.length === 0 && (
                <p className="text-xs text-gray-400">Aucun prix d'achat enregistré sur les bons de réception confirmés.</p>
            )}

            {rows.length > 0 && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        {[
                            { label: "Dernier HT", value: fmt(summary.last_unit_cost_ht) },
                            { label: "Moyenne pondérée HT", value: fmt(summary.weighted_avg_unit_cost_ht) },
                            { label: "Min HT", value: fmt(summary.min_unit_cost_ht) },
                            { label: "Max HT", value: fmt(summary.max_unit_cost_ht) },
                        ].map((s) => (
                            <div key={s.label} className="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2.5">
                                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{s.label}</div>
                                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">{s.value}</div>
                            </div>
                        ))}
                    </div>

                    <div className="overflow-x-auto rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] text-gray-400 uppercase tracking-wide">
                                    <th className="px-3 py-2">Date</th>
                                    <th className="px-3 py-2">Fournisseur</th>
                                    {hasVariants && <th className="px-3 py-2">Variante</th>}
                                    <th className="px-3 py-2 text-right">Qté</th>
                                    <th className="px-3 py-2 text-right">PU HT</th>
                                    <th className="px-3 py-2 text-right">PU TTC</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((r) => (
                                    <tr key={r.id} className="text-gray-700 dark:text-gray-300">
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {formatDate(r.date)}
                                            {r.reference && <div className="text-[10px] text-gray-400 font-mono">{r.reference}</div>}
                                        </td>
                                        <td className="px-3 py-2">{r.provider_name || "—"}</td>
                                        {hasVariants && <td className="px-3 py-2">{r.variant_name || "—"}</td>}
                                        <td className="px-3 py-2 text-right">{r.quantity}</td>
                                        <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{fmt(r.unit_cost_ht)}</td>
                                        <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{fmt(r.unit_cost_ttc)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default PurchasePricesSection;
